/**
 * Starting a project.
 *
 * The client is required and has to be one that already exists - a project is
 * billed against somebody, and a typed name that was never picked is not a
 * client. Everything else this dialog asks for now lives in `ProjectForm`,
 * which the edit dialog draws too: the fields drifted apart while there were
 * two of them, and the create side was the one that lost - it never offered the
 * description, the tags or the start date, all three of which the route has
 * always accepted, so a new project began life missing columns that could then
 * only be filled by dragging its card on the roadmap.
 *
 * What stays here is what only creating decides: the status a brand-new project
 * starts in, and the guard that there is a client to bill.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { createProject, type Project } from "../../../lib/projects";
import { Btn, Modal } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ProjectForm, blankProjectForm, projectCreateBody, type ProjectFormValues } from "./ProjectForm";

export function NewProjectModal({ onClose, onCreate }: { onClose: () => void; onCreate: (p: Project) => void }) {
  const t = useT();
  const [values, setValues] = useState<ProjectFormValues>(blankProjectForm);
  const [busy, setBusy] = useState(false);

  const canSubmit = !!values.name.trim() && !!values.clientId;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const p = await createProject({ ...projectCreateBody(values), status: "in_progress" });
      onCreate(p);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={t("projects.addTitle")} label={t("projects.addTitle")} onClose={onClose}>
      {/* The billing-type field is a `Pop` menu, and a `Pop` without a host
          above it is a control that can be focused and pressed and never
          opens, silently. `Modal` mounts none, and this dialog is opened from
          screens that do not all have one of their own - so it is mounted
          here rather than left to the caller. */}
      <PopHost>
        <form onSubmit={submit}>
          <ProjectForm value={values} onChange={setValues} mode="create" />

          <div className="crm-m-foot">
            <Btn type="button" onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </Btn>
            <Btn type="submit" tone="dark" disabled={busy || !canSubmit}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("common.add")}
            </Btn>
          </div>
        </form>
      </PopHost>
    </Modal>
  );
}
